import React from 'react';

/**
 * IntegrationCard - Card component for integration list view
 *
 * Features:
 * - Shows integration status (connected/not connected)
 * - Test connection button
 * - Delete credentials button
 * - Click to open modal
 * - Affiliate badge (if available)
 */
const IntegrationCard = ({
  integration,
  isConnected = false,
  lastTested = null,
  testStatus = null,
  onConnect,
  onTest,
  onDelete,
  isTesting = false
}) => {
  return (
    <div
      className={`integration-card hf-panel hf-lift${isConnected ? " hf-connected" : ""}`}
      style={{
        border: '1px solid var(--border-primary)',
        borderRadius: '12px',
        padding: '20px',
        background: 'var(--bg-tertiary)',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }}
      onClick={onConnect}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
      }}>
        {/* Left side - Icon and info */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{ fontSize: '32px', lineHeight: '1' }}>{integration.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h4 style={{
                margin: 0,
                color: 'var(--text-primary)',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                {integration.name}
              </h4>
              {integration.affiliate?.commission && (
                <span style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  background: 'var(--bg-accent-subtle)',
                  color: 'var(--accent-primary)',
                  borderRadius: '4px',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Affiliate
                </span>
              )}
            </div>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)', fontSize: '13px' }}>
              {integration.description}
            </p>
          </div>
        </div>

        {/* Right side - Status badge */}
        <div style={{ marginLeft: '16px' }}>
          {isConnected ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: 'var(--bg-success-subtle)',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--accent-success)'
            }}>
              <span style={{ fontSize: '10px' }}>●</span>
              Connected
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: 'var(--bg-secondary)',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--text-secondary)'
            }}>
              <span style={{ fontSize: '10px' }}>○</span>
              Not Connected
            </div>
          )}
        </div>
      </div>

      {/* Connected state - show test results and actions */}
      {isConnected && (
        <div style={{
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border-secondary)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Test status */}
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {lastTested && (
              <>
                Last tested: {new Date(lastTested).toLocaleDateString()}
                {testStatus === 'success' && <span style={{ color: 'var(--accent-success)', marginLeft: '8px' }}>✓</span>}
                {testStatus === 'failed' && <span style={{ color: 'var(--accent-danger)', marginLeft: '8px' }}>✗</span>}
              </>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Test Connection */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTest();
              }}
              disabled={isTesting}
              style={{
                padding: '6px 12px',
                background: 'var(--accent-primary)',
                border: 'none',
                borderRadius: '8px',
                color: 'var(--text-on-accent)',
                fontSize: '12px',
                fontWeight: '500',
                cursor: isTesting ? 'not-allowed' : 'pointer',
                opacity: isTesting ? 0.6 : 1
              }}
            >
              {isTesting ? 'Testing...' : '🔌 Test'}
            </button>

            {/* Delete */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Remove ${integration.name} credentials?`)) {
                  onDelete();
                }
              }}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: '1px solid var(--border-danger)',
                borderRadius: '8px',
                color: 'var(--accent-danger)',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Not connected state - show call to action */}
      {!isConnected && (
        <div style={{
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border-secondary)'
        }}>
          <button
            className="hf-press hf-focus"
            style={{
              width: '100%',
              padding: '10px',
              background: 'var(--accent-primary)',
              border: 'none',
              borderRadius: '8px',
              color: 'var(--text-on-accent)',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onConnect();
            }}
          >
            Connect {integration.name}
          </button>
        </div>
      )}
    </div>
  );
};

export default IntegrationCard;
